import React, { useState, useEffect, useContext } from "react";
import { Modal, Table, Button, Form } from "react-bootstrap";
import AuthContext from "../../context/auth/authContext";
import Pagination from "../../components/Layout/Pagination";

const RolePermissionsModal = ({ role, show, handleClose }) => {
    const authContext = useContext(AuthContext);
    const { 
        listRolePermissions, 
        fetchPermissions, 
        all_permissions = [], 
        role_permissions = [], 
        assignPermissions, 
        removePermissions,
        clearPermissions
    } = authContext;

    const [currentPage, setCurrentPage] = useState(1);
    const [assignedPermissions, setAssignedPermissions] = useState([]);
    const [searchTerm, setSearchTerm] = useState(""); // ✅ Add search term state

    useEffect(() => {
        if (!role || !role.roleCode) return;

        const fetchData = async () => {
            await fetchPermissions();
            await listRolePermissions(role.roleCode);
            setCurrentPage(1);
        };

        fetchData();
    }, [role]);

    useEffect(() => {
        if (role_permissions.length > 0) {
            setAssignedPermissions(role_permissions.map(rp => rp.id));
        }
    }, [role_permissions]);

    const handleTogglePermission = (permission) => {
        const isAssigned = assignedPermissions.includes(permission.id);

        setAssignedPermissions(prevPermissions =>
            isAssigned ? prevPermissions.filter(id => id !== permission.id) : [...prevPermissions, permission.id]
        );

        if (isAssigned) {
            removePermissions(role.roleCode, [permission.id]);
        } else {
            assignPermissions(role.roleCode, [permission.id]);
        }

        listRolePermissions(role.roleCode);
    };

    const handleModalClose = () => {
        setAssignedPermissions([]); // Reset local state
        clearPermissions();
        handleClose();
    };

    const itemsPerPage = 10;
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;

    // ✅ Filter Permissions Based on Search Term
    const filteredPermissions = all_permissions.filter(permission =>
        permission.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // ✅ Sort Permissions (Checked ones first)
    const sortedPermissions = [...filteredPermissions].sort((a, b) => {
        const isAssignedA = assignedPermissions.includes(a.id);
        const isAssignedB = assignedPermissions.includes(b.id);
        return isAssignedB - isAssignedA;
    });

    // ✅ Apply Pagination After Filtering & Sorting
    const currentPermissions = sortedPermissions.slice(indexOfFirstItem, indexOfLastItem);

    return (
        <Modal show={show} onHide={handleModalClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Role Permissions</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {/* ✅ Search Input */}
                <Form.Group className="mb-3">
                    <Form.Control
                        type="text"
                        placeholder="Search permissions..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                    />
                </Form.Group>

                <Table striped bordered hover>
                    <thead>
                        <tr>
                            <th>Permission Name</th>
                            <th>Assigned</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentPermissions.length === 0 ? (
                            <tr>
                                <td colSpan="2" className="text-center">No permissions found.</td>
                            </tr>
                        ) : (
                            currentPermissions.map((permission) => {
                                const isAssigned = assignedPermissions.includes(permission.id);
                                return (
                                    <tr key={permission.id}>
                                        <td>{permission.name}</td>
                                        <td className="text-center">
                                            <Form.Check
                                                type="switch"
                                                checked={isAssigned}
                                                onChange={() => handleTogglePermission(permission)}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </Table>

                {filteredPermissions.length > 0 && (
                    <Pagination
                        itemsPerPage={itemsPerPage}
                        totalItems={filteredPermissions.length}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        totalPages={Math.ceil(filteredPermissions.length / itemsPerPage)}
                    />
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleModalClose}>
                    Close
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default RolePermissionsModal;
